import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type FormEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
} from "react";
import { useLocation, useNavigate } from "react-router";
import { Button, Cluster, FormField, Modal, Select, Spinner, Stack, Textarea, useToast } from "@salesapp/ui";
import { ApiError } from "../lib/api";
import {
  loxleyApi,
  type FeedbackReason,
  type LoxleyAction,
  type LoxleyChart as Chart,
  type LoxleyChatMessage,
  type LoxleyGuide,
  type SavedPrompt,
} from "../lib/loxleyApi";
import { useOptionalGuide } from "../guide/GuideProvider";
import { useAuth } from "../shell/AuthProvider";
import { LoxleyCharacter, type LoxleyMood } from "./LoxleyCharacter";
import { chartAsText, LoxleyChart } from "./LoxleyChart";
import { MAX_PINNED, useOptionalLoxley, type LoxleyRecord } from "./LoxleyProvider";
import "./loxley.css";

type SpeechResultEvent = { results: ArrayLike<ArrayLike<{ transcript: string }> & { isFinal: boolean }> };
type Recognition = {
  lang: string;
  interimResults: boolean;
  continuous: boolean;
  onresult: ((event: SpeechResultEvent) => void) | null;
  onend: (() => void) | null;
  onerror: ((event: { error: string }) => void) | null;
  start: () => void;
  stop: () => void;
};

function speechRecognition(): (new () => Recognition) | null {
  const scope = window as unknown as { SpeechRecognition?: new () => Recognition; webkitSpeechRecognition?: new () => Recognition };
  return scope.SpeechRecognition ?? scope.webkitSpeechRecognition ?? null;
}

/**
 * Silence that means the person has finished, not a breath between words.
 * The browser's own end-of-speech is shorter than this and cuts them off.
 */
export const END_OF_SPEECH_MS = 2000;
/** Chrome drops a live session; wait before opening the microphone again. */
export const VOICE_RESTART_MS = 200;
const MAX_VOICE_RESTARTS = 8;
/** Speak with no words at all. Once they start, only END_OF_SPEECH_MS ends it. */
export const VOICE_START_MS = 45000;

type VoiceTurn = {
  stopped: boolean;
  carried: string;
  liveFinal: string;
  liveInterim: string;
  silence: number | null;
  restart: number | null;
  idle: number | null;
  restarts: number;
};

function heardText(turn: VoiceTurn): string {
  return `${turn.carried} ${turn.liveFinal} ${turn.liveInterim}`.replace(/\s+/g, " ").trim();
}

function clearVoiceTimers(turn: VoiceTurn) {
  if (turn.silence !== null) window.clearTimeout(turn.silence);
  if (turn.restart !== null) window.clearTimeout(turn.restart);
  if (turn.idle !== null) window.clearTimeout(turn.idle);
  turn.silence = null;
  turn.restart = null;
  turn.idle = null;
}

export const DISCLAIMER = "AI can make mistakes. Please review and validate the response to ensure its accuracy.";

const GREETING =
  "Hi, I'm Loxley, your Saleslox AI assistant and sales coach. Ask me anything about your pipeline, "
  + "or tell me what to do. "
  + "I'll show you every change before it's saved.";

export const SAMPLE_QUESTIONS = [
  "What should I focus on today?",
  "Which of my deals haven't moved in two weeks?",
  "How far am I from my target this month?",
  "Log a call with Kate at ABC Holdings at 3pm today",
  "Create a follow-up task for tomorrow at 10am",
  "Show my quotes waiting for approval",
];

const REASONS: Array<{ value: FeedbackReason; label: string }> = [
  { value: "wrong", label: "The answer was wrong" },
  { value: "wrong_record", label: "It picked the wrong record" },
  { value: "incomplete", label: "It was incomplete" },
  { value: "not_helpful", label: "It didn't help" },
  { value: "too_slow", label: "It was too slow" },
  { value: "unsafe", label: "It suggested something unsafe" },
  { value: "other", label: "Something else" },
];

function errorText(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  return "Loxley could not answer just now. Please try again.";
}

function resultLine(action: LoxleyAction): string {
  if (action.status === "done") return "Saved.";
  if (action.status === "cancelled") return "Cancelled.";
  const body = action.result?.body as { detail?: unknown } | undefined;
  const detail = typeof body?.detail === "string" ? body.detail : null;
  return detail ? `Not saved: ${detail}` : "Not saved. Open the record to check.";
}

function ActionCard({
  action,
  onConfirm,
  onCancel,
  busy,
}: {
  action: LoxleyAction;
  onConfirm: () => void;
  onCancel: () => void;
  busy: boolean;
}) {
  const pending = action.status === "pending";
  return (
    <div className={`loxley-action loxley-action--${action.status}`} role="group" aria-label={action.summary.title}>
      <Stack gap={2}>
        <Cluster gap={2} justify="space-between" align="center">
          <strong className="loxley-action-title">{action.summary.title}</strong>
          <span className="loxley-action-tab">{action.summary.tab}</span>
        </Cluster>
        {action.channel === "mcp" ? (
          <p className="loxley-action-note">Requested by a connected AI app.</p>
        ) : null}
        {action.summary.fields.length ? (
          <dl className="loxley-action-fields">
            {action.summary.fields.map(([label, value], index) => (
              <div key={`${label}-${index}`} className="loxley-action-field">
                <dt>{label}</dt>
                <dd>{value}</dd>
              </div>
            ))}
          </dl>
        ) : null}
        {pending ? (
          <Cluster gap={2}>
            <Button size="sm" onClick={onConfirm} disabled={busy}>
              {action.risk === "high" ? "Yes, do it" : "Confirm"}
            </Button>
            <Button size="sm" variant="ghost" onClick={onCancel} disabled={busy}>Cancel</Button>
          </Cluster>
        ) : (
          <p className={`loxley-action-result loxley-action-result--${action.status}`} role="status">
            {resultLine(action)}
          </p>
        )}
      </Stack>
    </div>
  );
}

type Entry = {
  id: string;
  role: "user" | "assistant";
  content: string;
  actions?: LoxleyAction[];
  /** Tour or help-article buttons Loxley offered with this reply. */
  guides?: LoxleyGuide[];
  /** Charts Loxley drew under this reply; sent back as text with the history. */
  charts?: Chart[];
  /** Turn-log row for this reply. Absent on the greeting and on errors. */
  turnId?: string;
  rating?: "up" | "down";
  intro?: boolean;
  /** Refused by the guard: kept on screen, never sent back as history. */
  blocked?: boolean;
  /** Names of the records added to the chat when this question was asked. */
  about?: string[];
};

const KIND_LABELS: Record<LoxleyRecord["kind"], string> = {
  company: "Company", contact: "Contact", lead: "Lead", opportunity: "Opportunity", product: "Product",
  quote: "Quote", order: "Order", invoice: "Invoice", agreement: "Agreement", ticket: "Ticket",
};

const NEW_CHAT_HINT = "Start a new chat. Clears this conversation and the records added to it, "
  + "so nothing carries over to your next question.";

function greeting(): Entry {
  return { id: newId(), role: "assistant", content: GREETING, intro: true };
}

/** The records in this chat, and the one on screen offered as "Add to chat". */
function ChatRecords({
  pinned,
  offered,
  onPin,
  onUnpin,
}: {
  pinned: LoxleyRecord[];
  offered: LoxleyRecord | null;
  onPin: (record: LoxleyRecord) => void;
  onUnpin: (record: LoxleyRecord) => void;
}) {
  const offer = offered && !pinned.some((item) => item.id === offered.id) ? offered : null;
  if (!pinned.length && !offer) return null;
  const full = pinned.length >= MAX_PINNED;
  return (
    <div className="loxley-records" role="group" aria-label="Records in this chat">
      {pinned.map((record) => (
        <span key={record.id} className="loxley-record">
          <span className="loxley-record-kind">{KIND_LABELS[record.kind]}</span>
          <span className="loxley-record-label">{record.label}</span>
          <button type="button" className="loxley-record-remove" onClick={() => onUnpin(record)}
            aria-label={`Remove ${record.label} from this chat`} title="Remove from this chat">
            <svg viewBox="0 0 24 24" aria-hidden="true" className="loxley-icon">
              <path d="M6 6l12 12M18 6 6 18" />
            </svg>
          </button>
        </span>
      ))}
      {offer ? (
        <button type="button" className="loxley-record loxley-record--offer" disabled={full}
          onClick={() => onPin(offer)}
          title={full
            ? `A chat holds up to ${MAX_PINNED} records. Remove one first.`
            : `Ask Loxley about this ${KIND_LABELS[offer.kind].toLowerCase()}. It stays in the chat until you remove it or start a new chat.`}>
          <svg viewBox="0 0 24 24" aria-hidden="true" className="loxley-icon loxley-record-plus">
            <path d="M12 5v14M5 12h14" />
          </svg>
          <span>Add <strong>{offer.label}</strong> to chat</span>
        </button>
      ) : null}
    </div>
  );
}

function Inline({ text }: { text: string }) {
  return (
    <>
      {text.split(/(\*\*[^*]+\*\*)/g).map((part, index) => (
        part.startsWith("**") && part.endsWith("**") && part.length > 4
          ? <strong key={index}>{part.slice(2, -2)}</strong>
          : <span key={index}>{part}</span>
      ))}
    </>
  );
}

type ReplyBlock = { kind: "text"; lines: string[] } | { kind: "table"; head: string[]; rows: string[][] };

const TABLE_DIVIDER = /^\s*\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)*\|?\s*$/;
const NUMERIC_CELL = /^[-+(]?[\p{Sc}]?\s?[\d.,\s]+[%kKmMbB)]?$/u;

function tableCells(line: string): string[] {
  return line.trim().replace(/^\|/, "").replace(/\|$/, "").split("|").map((cell) => cell.trim());
}

/** Split a reply into text and Markdown tables (a | row, then a | --- | row, then | rows). */
function replyBlocks(text: string): ReplyBlock[] {
  const lines = text.split("\n");
  const blocks: ReplyBlock[] = [];
  let index = 0;
  while (index < lines.length) {
    const line = lines[index];
    if (line.includes("|") && TABLE_DIVIDER.test(lines[index + 1] ?? "") && !TABLE_DIVIDER.test(line)) {
      const head = tableCells(line);
      const rows: string[][] = [];
      index += 2;
      while (index < lines.length && lines[index].includes("|") && lines[index].trim()) {
        const cells = tableCells(lines[index]);
        rows.push(head.map((_, column) => cells[column] ?? ""));
        index += 1;
      }
      blocks.push({ kind: "table", head, rows });
      continue;
    }
    const last = blocks[blocks.length - 1];
    if (last?.kind === "text") last.lines.push(line);
    else blocks.push({ kind: "text", lines: [line] });
    index += 1;
  }
  return blocks;
}

/**
 * Model replies are plain text, but models still emit **bold**, "### " headings and Markdown tables.
 * Render bold as <strong>, drop heading marks and draw tables as tables. Text only, never HTML, so a
 * reply (or a CRM note quoted in it) cannot inject markup.
 */
export function ReplyText({ text }: { text: string }) {
  const blocks = replyBlocks(text);
  return (
    <>
      {blocks.map((block, blockIndex) => {
        if (block.kind === "table") {
          const numeric = block.head.map((_, column) =>
            block.rows.length > 0 && block.rows.every((row) => !row[column] || NUMERIC_CELL.test(row[column])));
          return (
            <div key={blockIndex} className="loxley-reply-table-wrap">
              <table className="loxley-table loxley-reply-table">
                <thead>
                  <tr>
                    {block.head.map((cell, column) => (
                      <th key={column} scope="col" className={numeric[column] ? "loxley-num" : undefined}>
                        <Inline text={cell} />
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {block.rows.map((row, rowIndex) => (
                    <tr key={rowIndex}>
                      {row.map((cell, column) => (
                        <td key={column} className={numeric[column] ? "loxley-num" : undefined}>
                          <Inline text={cell} />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        }
        const lines = block.lines.map((line) => line.replace(/^#{1,6}\s+/, ""));
        const last = blockIndex === blocks.length - 1;
        return lines.map((line, lineIndex) => (
          <span key={`${blockIndex}-${lineIndex}`}>
            <Inline text={line} />
            {lineIndex < lines.length - 1 || !last ? "\n" : null}
          </span>
        ));
      })}
    </>
  );
}

// The panel width is a per-browser convenience, like hiding Loxley: localStorage, failing to the default.
function widthKey(userId: string): string {
  return `saleslox.loxley.width.${userId}`;
}

function readWidth(userId: string): number | null {
  try {
    const value = Number(window.localStorage.getItem(widthKey(userId)));
    return Number.isFinite(value) && value > 0 ? value : null;
  } catch {
    return null;
  }
}

function writeWidth(userId: string, width: number | null): void {
  try {
    if (width === null) window.localStorage.removeItem(widthKey(userId));
    else window.localStorage.setItem(widthKey(userId), String(Math.round(width)));
  } catch {
    /* storage unavailable: the width lasts for this page only */
  }
}

function remPx(): number {
  return parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
}

/** Keep the panel at least 20rem wide and leave the page at least 24rem beside it. */
function clampWidth(width: number): number {
  const rem = remPx();
  const min = 20 * rem;
  const max = Math.max(min, window.innerWidth - 24 * rem);
  return Math.min(Math.max(width, min), max);
}

let nextId = 0;
const newId = () => `m${(nextId += 1)}`;

function CopyButton({ text, what }: { text: string; what: "question" | "answer" }) {
  const toast = useToast();
  return (
    <button
      type="button"
      className="loxley-icon-button"
      aria-label={`Copy ${what}`}
      title={`Copy ${what}`}
      onClick={() => {
        navigator.clipboard
          ?.writeText(text)
          .then(() => toast.show("Copied.", "success"))
          .catch(() => toast.show("Copy did not work. Select the text and copy it.", "danger"));
      }}
    >
      <svg viewBox="0 0 24 24" aria-hidden="true" className="loxley-icon">
        <rect x="9" y="9" width="11" height="11" rx="2" />
        <path d="M5 15V6a2 2 0 0 1 2-2h9" />
      </svg>
    </button>
  );
}

function SavePromptButton({ saved, onClick }: { saved: boolean; onClick: () => void }) {
  const label = saved ? "Saved to your prompts" : "Save prompt";
  return (
    <button
      type="button"
      className={`loxley-icon-button${saved ? " is-active" : ""}`}
      aria-label={label}
      aria-pressed={saved}
      title={label}
      onClick={onClick}
    >
      <svg viewBox="0 0 24 24" aria-hidden="true" className="loxley-icon">
        <path d="M6 4h12v16l-6-4-6 4V4Z" />
      </svg>
    </button>
  );
}

function ThumbButton({ up, active, onClick }: { up: boolean; active: boolean; onClick: () => void }) {
  const label = up ? "Good answer" : "Poor answer";
  return (
    <button
      type="button"
      className={`loxley-icon-button${active ? " is-active" : ""}`}
      aria-label={label}
      aria-pressed={active}
      title={label}
      onClick={onClick}
    >
      <svg viewBox="0 0 24 24" aria-hidden="true" className={`loxley-icon${up ? "" : " loxley-icon--down"}`}>
        <path d="M7 11v9H4v-9h3Zm0 0 4-7a2 2 0 0 1 3 2l-1 4h5a2 2 0 0 1 2 2l-2 7a2 2 0 0 1-2 1H7" />
      </svg>
    </button>
  );
}

/**
 * A tour Loxley offers starts the same guided tour as avatar -> Guided tours, and closes the panel
 * so the spotlight is not hidden behind it. A tour this user cannot see renders nothing.
 */
function GuideButton({ guide, onOpened }: { guide: LoxleyGuide; onOpened: () => void }) {
  const tours = useOptionalGuide();
  const navigate = useNavigate();
  if (guide.kind === "tour") {
    const tour = tours?.available.find((item) => item.id === guide.id);
    if (!tour || !tours) return null;
    return (
      <Button size="sm" variant="secondary" onClick={() => { onOpened(); tours.start(tour.id); }}>
        Start the tour: {tour.title}
      </Button>
    );
  }
  const query = new URLSearchParams({ guide: guide.guide, topic: guide.id });
  return (
    <Button size="sm" variant="secondary" onClick={() => { onOpened(); navigate(`/help?${query.toString()}`); }}>
      Open help: {guide.title}
    </Button>
  );
}

export function LoxleyAssistant() {
  const loxley = useOptionalLoxley();
  const { session } = useAuth();
  const toast = useToast();
  const location = useLocation();
  const orgId = session?.orgId ?? "";
  const userId = session?.userId ?? "";
  const [entries, setEntries] = useState<Entry[]>(() => [greeting()]);
  // Bumped by New chat, so a reply still on its way lands nowhere instead of in the fresh chat.
  const chatSession = useRef(0);
  const [inbox, setInbox] = useState<LoxleyAction[]>([]);
  const [draft, setDraft] = useState("");
  const [thinking, setThinking] = useState(false);
  const [listening, setListening] = useState(false);
  const [busyAction, setBusyAction] = useState<string | null>(null);
  const [menu, setMenu] = useState<{ x: number; y: number } | null>(null);
  const [feedbackFor, setFeedbackFor] = useState<Entry | null>(null);
  const [reason, setReason] = useState<FeedbackReason | "">("");
  const [comment, setComment] = useState("");
  const SpeechCtor = speechRecognition();
  const recognition = useRef<Recognition | null>(null);
  const voice = useRef<VoiceTurn | null>(null);
  const sendRef = useRef<(text: string) => void>(() => {});
  const beginRecognitionRef = useRef<() => void>(() => {});
  const speechCtorRef = useRef(SpeechCtor);
  speechCtorRef.current = SpeechCtor;
  const toastRef = useRef(toast);
  toastRef.current = toast;
  const scroller = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLTextAreaElement>(null);
  const panel = useRef<HTMLElement>(null);
  const [width, setWidth] = useState<number | null>(null);
  const [resizing, setResizing] = useState(false);
  const [samplesOpen, setSamplesOpen] = useState(false);
  const [saved, setSaved] = useState<SavedPrompt[]>([]);
  const open = Boolean(loxley?.open);

  const loadInbox = useCallback(() => {
    if (!orgId) return;
    loxleyApi.pending(orgId).then(setInbox).catch(() => setInbox([]));
  }, [orgId]);

  useEffect(() => {
    if (!open) return;
    loadInbox();
    input.current?.focus();
    if (orgId) loxleyApi.savedPrompts(orgId).then(setSaved).catch(() => setSaved([]));
  }, [open, loadInbox, orgId]);

  async function savePrompt(text: string) {
    try {
      const row = await loxleyApi.savePrompt(orgId, text);
      setSaved((current) => [row, ...current.filter((item) => item.id !== row.id)]);
      toast.show("Saved. Find it at the top of Sample questions.", "success");
    } catch (error) {
      toast.show(errorText(error), "danger");
    }
  }

  async function removePrompt(item: SavedPrompt) {
    try {
      await loxleyApi.deletePrompt(orgId, item.id);
      setSaved((current) => current.filter((row) => row.id !== item.id));
    } catch (error) {
      toast.show(errorText(error), "danger");
    }
  }

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || feedbackFor) return;
      // Escape closes the innermost thing first: the sample-question flyout, then the panel.
      if (samplesOpen) setSamplesOpen(false);
      else loxley?.setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, feedbackFor, samplesOpen, loxley]);

  useEffect(() => {
    if (!open) setSamplesOpen(false);
  }, [open]);

  useEffect(() => {
    setWidth(userId ? readWidth(userId) : null);
  }, [userId]);

  // The panel docks beside the page rather than over it: the shell reads these from the root
  // element and gives up the panel's width (see loxley.css). Narrow screens still overlay.
  const shown = open && Boolean(loxley?.available) && !loxley?.hidden && Boolean(orgId);
  useEffect(() => {
    const root = document.documentElement;
    if (width === null) root.style.removeProperty("--loxley-panel-width");
    else root.style.setProperty("--loxley-panel-width", `${clampWidth(width)}px`);
    if (shown) root.dataset.loxleyDocked = "true";
    else delete root.dataset.loxleyDocked;
    if (resizing) root.dataset.loxleyResizing = "true";
    else delete root.dataset.loxleyResizing;
  }, [width, shown, resizing]);

  useEffect(() => () => {
    const root = document.documentElement;
    root.style.removeProperty("--loxley-panel-width");
    delete root.dataset.loxleyDocked;
    delete root.dataset.loxleyResizing;
  }, []);

  useEffect(() => {
    if (width === null) return;
    // Re-clamp when the window shrinks so the page always keeps its share.
    const onResize = () => setWidth((current) => (current === null ? null : clampWidth(current)));
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [width]);

  function commitWidth(next: number | null) {
    const value = next === null ? null : clampWidth(next);
    setWidth(value);
    if (userId) writeWidth(userId, value);
  }

  function onResizeStart(event: ReactPointerEvent<HTMLDivElement>) {
    if (event.button !== 0) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    setResizing(true);
  }

  function onResizeMove(event: ReactPointerEvent<HTMLDivElement>) {
    if (!resizing) return;
    setWidth(clampWidth(window.innerWidth - event.clientX));
  }

  function onResizeEnd(event: ReactPointerEvent<HTMLDivElement>) {
    if (!resizing) return;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
    setResizing(false);
    commitWidth(window.innerWidth - event.clientX);
  }

  function onResizeKey(event: ReactKeyboardEvent<HTMLDivElement>) {
    const current = width ?? panel.current?.getBoundingClientRect().width ?? 24 * remPx();
    const step = 2 * remPx();
    if (event.key === "ArrowLeft") commitWidth(current + step);
    else if (event.key === "ArrowRight") commitWidth(current - step);
    else if (event.key === "Home" || event.key === "Enter") commitWidth(null);
    else return;
    event.preventDefault();
  }

  useEffect(() => {
    const node = scroller.current;
    if (node) node.scrollTop = node.scrollHeight;
  }, [entries, inbox, thinking]);

  useEffect(() => {
    if (!menu) return;
    const close = () => setMenu(null);
    window.addEventListener("click", close);
    window.addEventListener("keydown", close);
    return () => {
      window.removeEventListener("click", close);
      window.removeEventListener("keydown", close);
    };
  }, [menu]);

  const send = useCallback(async (text: string) => {
    const message = text.trim();
    if (!message || thinking || !orgId) return;
    const history: LoxleyChatMessage[] = entries
      .filter((entry) => !entry.intro && !entry.blocked && entry.content)
      .slice(-12)
      .map((entry) => ({
        role: entry.role,
        content: [entry.content, ...(entry.charts ?? []).map(chartAsText)].join("\n\n"),
      }));
    const askedId = newId();
    const pinned = loxley?.pinned ?? [];
    const asked = chatSession.current;
    setEntries((current) => [...current, {
      id: askedId, role: "user", content: message, about: pinned.length ? pinned.map((item) => item.label) : undefined,
    }]);
    setDraft("");
    setThinking(true);
    try {
      const turn = await loxleyApi.chat(
        orgId, message, history, location.pathname, pinned.map(({ kind, id }) => ({ kind, id })),
      );
      if (asked !== chatSession.current) return;
      const blocked = Boolean(turn.blocked);
      setEntries((current) => [
        ...current.map((entry) => (blocked && entry.id === askedId ? { ...entry, blocked } : entry)),
        {
          id: newId(), role: "assistant", content: turn.reply, actions: turn.actions, guides: turn.guides,
          charts: turn.charts, turnId: turn.turn_id, blocked,
        },
      ]);
      loxley?.refresh();
    } catch (error) {
      if (asked !== chatSession.current) return;
      setEntries((current) => [...current, { id: newId(), role: "assistant", content: errorText(error) }]);
    } finally {
      if (asked === chatSession.current) setThinking(false);
    }
  }, [entries, location.pathname, orgId, thinking, loxley]);

  sendRef.current = send;

  const endVoice = useCallback((submit: boolean) => {
    const turn = voice.current;
    if (!turn || turn.stopped) return;
    turn.stopped = true;
    clearVoiceTimers(turn);
    const text = heardText(turn);
    try { recognition.current?.stop(); } catch { /* the browser already ended this session */ }
    setListening(false);
    if (submit && text) void sendRef.current(text);
  }, []);

  const beginRecognition = useCallback(() => {
    const Ctor = speechCtorRef.current;
    const turn = voice.current;
    if (!Ctor || !turn || turn.stopped) return;
    const recogniser = new Ctor();
    recogniser.lang = navigator.language || "en-US";
    recogniser.interimResults = true;
    // continuous: a short pause must not end the session. We decide when they have finished.
    recogniser.continuous = true;

    const scheduleRestart = () => {
      if (turn.stopped || voice.current !== turn) return;
      if (turn.restart !== null) window.clearTimeout(turn.restart);
      turn.restart = window.setTimeout(() => {
        turn.restart = null;
        beginRecognitionRef.current();
      }, VOICE_RESTART_MS);
    };

    // The browser ends the session on a breath, a blip, or its own silence timer.
    // That is not the person finishing. Keep what they said and listen again.
    const dropped = () => {
      if (voice.current !== turn || turn.stopped || recognition.current !== recogniser) return;
      turn.carried = heardText(turn);
      turn.liveFinal = "";
      turn.liveInterim = "";
      if (!turn.carried) {
        turn.restarts += 1;
        if (turn.restarts > MAX_VOICE_RESTARTS) {
          endVoice(false);
          return;
        }
      }
      scheduleRestart();
    };

    recogniser.onresult = (event) => {
      if (voice.current !== turn || turn.stopped) return;
      let liveFinal = "";
      let liveInterim = "";
      for (let i = 0; i < event.results.length; i += 1) {
        const result = event.results[i];
        const piece = result[0]?.transcript ?? "";
        if (result.isFinal) liveFinal += piece;
        else liveInterim += piece;
      }
      turn.liveFinal = liveFinal;
      turn.liveInterim = liveInterim;
      turn.restarts = 0;
      if (turn.idle !== null) {
        window.clearTimeout(turn.idle);
        turn.idle = null;
      }
      const text = heardText(turn);
      setDraft(text);
      if (!text) return;
      if (turn.silence !== null) window.clearTimeout(turn.silence);
      turn.silence = window.setTimeout(() => {
        if (voice.current === turn) endVoice(true);
      }, END_OF_SPEECH_MS);
    };
    recogniser.onerror = (event) => {
      if (voice.current !== turn || turn.stopped) return;
      // no-speech and network still fire onend, which keeps the microphone open.
      if (event.error === "aborted" || event.error === "no-speech" || event.error === "network") return;
      endVoice(false);
      toastRef.current.show(
        event.error === "not-allowed" || event.error === "service-not-allowed"
          ? "Loxley can't use your microphone. Allow it in your browser's site settings and try again."
          : "Voice input did not work. Type your request instead.",
        "danger",
      );
    };
    recogniser.onend = () => dropped();

    recognition.current = recogniser;
    try {
      recogniser.start();
    } catch {
      dropped();
    }
  }, [endVoice]);

  beginRecognitionRef.current = beginRecognition;

  useEffect(() => {
    // Closing or hiding the panel must release the microphone. A paused request is not sent.
    if (!open || !loxley?.available || loxley.hidden) endVoice(false);
  }, [open, loxley?.available, loxley?.hidden, endVoice]);

  useEffect(() => () => endVoice(false), [endVoice]);

  function composerText(): string {
    const live = voice.current;
    if (live && !live.stopped) return heardText(live) || draft;
    return draft;
  }

  function submitComposer() {
    const text = composerText();
    endVoice(false);
    void send(text);
  }

  function newChat() {
    chatSession.current += 1;
    endVoice(false);
    setEntries([greeting()]);
    setDraft("");
    setThinking(false);
    setSamplesOpen(false);
    setFeedbackFor(null);
    loxley?.clearPinned();
    input.current?.focus();
  }

  function replaceAction(next: LoxleyAction) {
    setEntries((current) => current.map((entry) => (
      entry.actions ? { ...entry, actions: entry.actions.map((a) => (a.id === next.id ? next : a)) } : entry
    )));
    setInbox((current) => current.filter((a) => a.id !== next.id));
  }

  async function confirm(action: LoxleyAction) {
    setBusyAction(action.id);
    try {
      replaceAction(await loxleyApi.confirm(orgId, action.id));
    } catch (error) {
      toast.show(errorText(error), "danger");
      loadInbox();
    } finally {
      setBusyAction(null);
    }
  }

  async function cancel(action: LoxleyAction) {
    setBusyAction(action.id);
    try {
      await loxleyApi.cancel(orgId, action.id);
      replaceAction({ ...action, status: "cancelled" });
    } catch (error) {
      toast.show(errorText(error), "danger");
    } finally {
      setBusyAction(null);
    }
  }

  function setRating(entry: Entry, rating: "up" | "down") {
    setEntries((current) => current.map((item) => (item.id === entry.id ? { ...item, rating } : item)));
  }

  async function rate(entry: Entry, rating: "up" | "down") {
    if (!entry.turnId) return;
    setRating(entry, rating);
    try {
      // Record the thumb straight away; the pop-up only adds an optional reason and comment.
      await loxleyApi.feedback(orgId, entry.turnId, rating);
      if (rating === "down") {
        setReason("");
        setComment("");
        setFeedbackFor(entry);
      } else {
        toast.show("Thanks for the feedback.", "success");
      }
    } catch (error) {
      toast.show(errorText(error), "danger");
    }
  }

  async function sendFeedbackDetail() {
    const entry = feedbackFor;
    setFeedbackFor(null);
    if (!entry?.turnId || (!reason && !comment.trim())) return;
    try {
      await loxleyApi.feedback(orgId, entry.turnId, "down", reason || null, comment);
      toast.show("Thanks. This helps us improve Loxley.", "success");
    } catch (error) {
      toast.show(errorText(error), "danger");
    }
  }

  function toggleVoice() {
    if (!speechCtorRef.current) return;
    if (voice.current && !voice.current.stopped) {
      // Stop means they are finished, including words the browser has not finalised yet.
      endVoice(true);
      return;
    }
    const turn: VoiceTurn = {
      stopped: false, carried: "", liveFinal: "", liveInterim: "",
      silence: null, restart: null, idle: null, restarts: 0,
    };
    turn.idle = window.setTimeout(() => {
      if (voice.current === turn && !heardText(turn)) endVoice(false);
    }, VOICE_START_MS);
    voice.current = turn;
    setListening(true);
    beginRecognition();
  }

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    submitComposer();
  }

  function onContextMenu(event: ReactMouseEvent) {
    event.preventDefault();
    setMenu({ x: event.clientX, y: event.clientY });
  }

  function hide() {
    setMenu(null);
    loxley?.setHidden(true);
    toast.show("Loxley is hidden. Bring Loxley back from your account menu.", "info");
  }

  if (!loxley?.available || loxley.hidden || !orgId) return null;

  const mood: LoxleyMood = listening ? "listening" : thinking ? "thinking" : "idle";
  const fresh = !entries.some((entry) => entry.role === "user");

  return (
    <>
      <aside
        ref={panel}
        className={`loxley-panel${open ? " is-open" : ""}`}
        aria-label="Loxley, your sales coach"
        aria-hidden={!open}
        inert={!open}
      >
        <div
          className={`loxley-resize${resizing ? " is-dragging" : ""}`}
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize Loxley. Drag, or use the left and right arrow keys. Double-click to reset."
          title="Drag to resize. Double-click to reset."
          tabIndex={0}
          onPointerDown={onResizeStart}
          onPointerMove={onResizeMove}
          onPointerUp={onResizeEnd}
          onPointerCancel={onResizeEnd}
          onDoubleClick={() => commitWidth(null)}
          onKeyDown={onResizeKey}
        >
          <span className="loxley-resize-grip" aria-hidden="true" />
        </div>
        <header className="loxley-panel-header">
          <Cluster gap={3} align="center">
            <LoxleyCharacter mood={mood} className="loxley-panel-avatar" />
            <Stack gap={1}>
              <strong>Meet Loxley</strong>
              <span className="loxley-panel-sub">Your Saleslox AI assistant</span>
              <button
                type="button"
                className="loxley-samples-toggle"
                aria-expanded={samplesOpen}
                aria-controls="loxley-samples-flyout"
                onClick={() => setSamplesOpen((current) => !current)}
              >
                Sample questions
              </button>
            </Stack>
          </Cluster>
          <Cluster gap={1} align="center">
            <Button size="sm" variant="ghost" onClick={newChat} title={NEW_CHAT_HINT}
              aria-label="New chat: clears this conversation and the records added to it"
              disabled={fresh && !loxley.pinned.length && !draft}>
              New chat
            </Button>
            <Button size="sm" variant="ghost" onClick={() => loxley.setOpen(false)} aria-label="Close Loxley">
              Close
            </Button>
          </Cluster>
        </header>
        {samplesOpen ? (
          <section id="loxley-samples-flyout" className="loxley-samples-flyout" aria-label="Sample questions">
            <Cluster gap={2} justify="space-between" align="center">
              <strong>Sample questions</strong>
              <Button size="sm" variant="ghost" onClick={() => setSamplesOpen(false)}
                aria-label="Close sample questions">
                Close
              </Button>
            </Cluster>
            {saved.length ? (
              <Stack gap={2}>
                <p className="loxley-samples-title">Your saved prompts</p>
                {saved.map((item) => (
                  <div key={item.id} className="loxley-saved">
                    <button type="button" className="loxley-sample loxley-saved-prompt" disabled={thinking}
                      onClick={() => { setSamplesOpen(false); void send(item.prompt); }}>
                      {item.prompt}
                    </button>
                    <button type="button" className="loxley-icon-button" aria-label={`Remove saved prompt: ${item.prompt}`}
                      title="Remove" onClick={() => void removePrompt(item)}>
                      <svg viewBox="0 0 24 24" aria-hidden="true" className="loxley-icon">
                        <path d="M6 6l12 12M18 6 6 18" />
                      </svg>
                    </button>
                  </div>
                ))}
              </Stack>
            ) : (
              <p className="loxley-panel-sub">
                Save a question you ask often with the bookmark under it. Saved prompts appear here.
              </p>
            )}
            <Stack gap={2}>
              <p className="loxley-samples-title">Try asking</p>
              {SAMPLE_QUESTIONS.map((question) => (
                <button key={question} type="button" className="loxley-sample" disabled={thinking}
                  onClick={() => { setSamplesOpen(false); void send(question); }}>
                  {question}
                </button>
              ))}
            </Stack>
          </section>
        ) : null}
        <div className="loxley-thread" ref={scroller} aria-live="polite">
          <Stack gap={3}>
            {inbox.length ? (
              <Stack gap={2}>
                <p className="loxley-inbox-title">Waiting for your approval</p>
                {inbox.map((action) => (
                  <ActionCard key={action.id} action={action} busy={busyAction === action.id}
                    onConfirm={() => void confirm(action)} onCancel={() => void cancel(action)} />
                ))}
              </Stack>
            ) : null}
            {entries.map((entry) => (
              <Stack gap={2} key={entry.id} className={`loxley-entry loxley-entry--${entry.role}`}>
                {entry.content ? (
                  <div className={`loxley-bubble loxley-bubble--${entry.role}`}>
                    <div className="loxley-bubble-text">
                      {entry.role === "assistant" ? <ReplyText text={entry.content} /> : entry.content}
                    </div>
                  </div>
                ) : null}
                {entry.about?.length ? (
                  <p className="loxley-entry-about">About {entry.about.join(", ")}</p>
                ) : null}
                {entry.charts?.map((chart, index) => <LoxleyChart key={`${chart.title}-${index}`} chart={chart} />)}
                {entry.actions?.map((action) => (
                  <ActionCard key={action.id} action={action} busy={busyAction === action.id}
                    onConfirm={() => void confirm(action)} onCancel={() => void cancel(action)} />
                ))}
                {entry.guides?.length ? (
                  <Cluster gap={2} className="loxley-guides">
                    {entry.guides.map((guide) => (
                      <GuideButton key={`${guide.kind}-${guide.id}`} guide={guide} onOpened={() => loxley.setOpen(false)} />
                    ))}
                  </Cluster>
                ) : null}
                {entry.content && !entry.intro ? (
                  <Cluster gap={1} className="loxley-entry-tools">
                    <CopyButton text={entry.content} what={entry.role === "user" ? "question" : "answer"} />
                    {entry.role === "user" && !entry.blocked ? (
                      <SavePromptButton
                        saved={saved.some((item) => item.prompt === entry.content.split(/\s+/).join(" ").trim())}
                        onClick={() => void savePrompt(entry.content)}
                      />
                    ) : null}
                    {entry.role === "assistant" && entry.turnId ? (
                      <>
                        <ThumbButton up active={entry.rating === "up"} onClick={() => void rate(entry, "up")} />
                        <ThumbButton up={false} active={entry.rating === "down"}
                          onClick={() => void rate(entry, "down")} />
                      </>
                    ) : null}
                  </Cluster>
                ) : null}
              </Stack>
            ))}
            {fresh ? (
              <Stack gap={2} className="loxley-samples">
                <p className="loxley-samples-title">Try asking</p>
                {SAMPLE_QUESTIONS.map((question) => (
                  <button key={question} type="button" className="loxley-sample" onClick={() => void send(question)}
                    disabled={thinking}>
                    {question}
                  </button>
                ))}
              </Stack>
            ) : null}
            {thinking ? (
              <Cluster gap={2} align="center" className="loxley-thinking">
                <Spinner size={16} />
                <span>Loxley is working on it…</span>
              </Cluster>
            ) : null}
          </Stack>
        </div>
        <form className="loxley-compose" onSubmit={onSubmit}>
          <Stack gap={2}>
            <ChatRecords pinned={loxley.pinned} offered={loxley.offered} onPin={loxley.pin}
              onUnpin={(record) => loxley.unpin(record.id)} />
            <Textarea
              ref={input}
              aria-label="Ask Loxley"
              placeholder={listening
                ? "Listening…"
                : loxley.pinned.length
                  ? `Ask about ${loxley.pinned.map((item) => item.label).join(", ")}`
                  : "Ask Loxley or tell it what to do"}
              value={draft}
              rows={3}
              maxLength={2000}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter" && !event.shiftKey) {
                  event.preventDefault();
                  submitComposer();
                }
              }}
            />
            <Cluster gap={2} justify="end" align="center">
                {SpeechCtor ? (
                  <Button type="button" size="sm" variant={listening ? "danger" : "secondary"} onClick={toggleVoice}
                    aria-pressed={listening} aria-label={listening ? "Stop listening" : "Speak to Loxley"}>
                    {listening ? "Stop" : "Speak"}
                  </Button>
                ) : null}
                <Button type="submit" size="sm" disabled={thinking || !draft.trim()}>Send</Button>
            </Cluster>
            <p className="loxley-disclaimer">{DISCLAIMER}</p>
          </Stack>
        </form>
      </aside>
      {open ? null : (
        <button
          type="button"
          className="loxley-launcher"
          aria-label="Open Loxley, your sales coach. Right-click to hide."
          aria-expanded={false}
          title="Loxley, your sales coach (right-click to hide)"
          onClick={() => loxley.setOpen(true)}
          onContextMenu={onContextMenu}
        >
          <LoxleyCharacter mood={mood} />
        </button>
      )}
      {menu ? (
        <div
          className="loxley-context-menu"
          role="menu"
          style={{ left: `calc(${menu.x} * 1px)`, top: `calc(${menu.y} * 1px)` }}
          onClick={(event) => event.stopPropagation()}
        >
          <button type="button" role="menuitem" className="loxley-context-item"
            onClick={() => { setMenu(null); loxley.setOpen(true); }}>
            Open Loxley
          </button>
          <button type="button" role="menuitem" className="loxley-context-item" onClick={hide}>
            Hide Loxley
          </button>
        </div>
      ) : null}
      <Modal
        isOpen={Boolean(feedbackFor)}
        onClose={() => setFeedbackFor(null)}
        title="What went wrong?"
        footer={
          <Cluster gap={2} justify="end">
            <Button variant="ghost" onClick={() => setFeedbackFor(null)}>Skip</Button>
            <Button onClick={() => void sendFeedbackDetail()} disabled={!reason && !comment.trim()}>
              Send feedback
            </Button>
          </Cluster>
        }
      >
        <Stack gap={3}>
          <p className="loxley-panel-sub">
            Your thumbs down is already recorded. Telling us more is optional and helps us fix it.
          </p>
          <FormField label="Reason" htmlFor="loxley-feedback-reason">
            <Select id="loxley-feedback-reason" value={reason}
              onChange={(event) => setReason(event.target.value as FeedbackReason | "")}>
              <option value="">Choose a reason (optional)</option>
              {REASONS.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
            </Select>
          </FormField>
          <FormField label="Anything else? (optional)" htmlFor="loxley-feedback-comment">
            <Textarea id="loxley-feedback-comment" rows={4} maxLength={2000} value={comment}
              placeholder="What did you expect Loxley to do?"
              onChange={(event) => setComment(event.target.value)} />
          </FormField>
        </Stack>
      </Modal>
    </>
  );
}
